import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Clock, ExternalLink, Search } from 'lucide-react'
import { EmptyState } from '@/components/shared/empty-state'
import { Input } from '@/components/ui/input'
import { RecordStatusBadge } from '@/features/records/components/record-status-badge'
import { RecordWorkspaceShell } from '@/features/records/components/record-workspace-shell'
import { useRecordsQuery } from '@/features/records/api/use-records-query'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { cn } from '@/lib/utils/cn'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

dayjs.extend(relativeTime)

// ─── PendingListPanel ─────────────────────────────────────────────────────────

interface PendingListPanelProps {
  projectId: string
  selectedRecordId: string | null
  onSelectRecord: (id: string) => void
}

function PendingListPanel({ projectId, selectedRecordId, onSelectRecord }: PendingListPanelProps) {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 300)
  const { data, isLoading } = useRecordsQuery(
    projectId,
    {
      status: ['submitted_for_review', 'under_review'],
      search: debouncedSearch || undefined,
    },
    0,
    50
  )

  const records = data?.records ?? []

  return (
    <div className="w-full h-full flex flex-col border-e border-border overflow-hidden">
      {/* Header */}
      <div className="shrink-0 px-4 py-3 border-b border-border flex items-center gap-2">
        <Clock className="w-4 h-4 text-muted-foreground shrink-0" />
        <span className="text-sm font-medium flex-1">در انتظار بررسی</span>
        {records.length > 0 && (
          <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded-full text-muted-foreground">
            {records.length.toLocaleString('fa-IR')}
          </span>
        )}
      </div>

      {/* Search */}
      <div className="shrink-0 p-3 border-b border-border">
        <div className="relative">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="جستجو در شناسه و فیلدها..."
            className="ps-8 h-8 text-sm"
            dir="auto"
          />
        </div>
      </div>

      {/* Scrollable list */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-3 space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-14 bg-muted rounded animate-pulse" />
            ))}
          </div>
        ) : records.length === 0 ? (
          <EmptyState
            icon={Clock}
            title="هیچ موردی در انتظار بررسی نیست"
            className="py-12"
          />
        ) : (
          <div className="divide-y divide-border">
            {records.map((record) => {
              const waitDays = record.submitted_at
                ? dayjs().diff(dayjs(record.submitted_at), 'day')
                : 0
              const isLate = waitDays > 7

              return (
                <div
                  key={record.id}
                  className={cn(
                    'flex items-stretch hover:bg-muted/20 transition-colors group',
                    selectedRecordId === record.id && 'bg-muted/40 border-s-2 border-primary'
                  )}
                >
                  <button
                    className="flex-1 text-start px-3 py-3 min-w-0"
                    onClick={() => onSelectRecord(record.id)}
                  >
                    <p className="font-mono text-xs font-medium">{record.display_id}</p>
                    <div className="flex items-center justify-between gap-1 mt-1">
                      <RecordStatusBadge status={record.status} />
                      {record.submitted_at && (
                        <span className={cn('text-[10px]', isLate ? 'text-red-400' : 'text-muted-foreground')}>
                          {dayjs(record.submitted_at).fromNow()}
                        </span>
                      )}
                    </div>
                  </button>
                  <a
                    href={`/projects/${projectId}/records/${record.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center px-2 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
                    title="باز کردن در تب جدید"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── PendingReviewPage ────────────────────────────────────────────────────────

export function PendingReviewPage() {
  const { projectId } = useParams<{ projectId: string }>()

  if (!projectId) return null

  return (
    <RecordWorkspaceShell
      projectId={projectId}
      leftPanel={({ selectedRecordId, onSelectRecord }) => (
        <PendingListPanel
          projectId={projectId}
          selectedRecordId={selectedRecordId}
          onSelectRecord={onSelectRecord}
        />
      )}
    />
  )
}
